import { Link } from 'react-router-dom';
import { Sparkles, AlertTriangle, Lock, X } from 'lucide-react';
import { useState } from 'react';
import { useTenantAccess } from '@/hooks/useTenantAccess';

/**
 * Banner topo do app. 4 modos:
 *   - trial vigente confortável (>3 dias): info azul (descartável)
 *   - trial vigente urgente (<= 3 dias): alerta âmbar (descartável)
 *   - read_only (trial em graça): vermelho, não descartável — paywall global
 *   - locked: vermelho intenso — paywall global
 */
export function TrialBanner() {
  const [dismissed, setDismissed] = useState(false);
  const { data } = useTenantAccess();

  if (!data) return null;

  // Full + sem trial = nada a mostrar
  if (data.level === 'full' && data.trialDaysLeft == null) return null;

  // Trial confortável e descartado
  if (data.level === 'full' && data.trialDaysLeft! > 3 && dismissed) return null;

  if (data.level === 'locked') {
    return (
      <div className="bg-red-700 border-b-2 border-red-900 px-4 py-2.5">
        <div className="flex items-center justify-center gap-3 text-sm text-white">
          <Lock className="h-4 w-4" />
          <span>
            <strong>Sua conta está inativa.</strong> Reative sua assinatura para voltar a editar conteúdo.
          </span>
          <Link to="/billing" className="ml-2 underline font-bold hover:no-underline">
            Reativar agora →
          </Link>
        </div>
      </div>
    );
  }

  if (data.level === 'read_only') {
    const grace = data.graceDaysLeft;
    return (
      <div className="bg-red-50 border-b-2 border-red-300 px-4 py-2.5">
        <div className="flex items-center justify-center gap-3 text-sm">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <span className="text-red-900">
            <strong>Trial encerrado.</strong>{' '}
            {grace != null && grace > 0
              ? <>Suas TVs continuam tocando por mais {grace} {grace === 1 ? 'dia' : 'dias'}, mas não dá para editar.</>
              : <>Suas TVs vão exibir o aviso MyShow em breve.</>}
          </span>
          <Link to="/billing" className="ml-2 underline font-bold text-red-700 hover:no-underline">
            Assinar agora →
          </Link>
        </div>
      </div>
    );
  }

  // level === 'full' com trial vigente
  const daysLeft = data.trialDaysLeft!;
  const urgent = daysLeft <= 3;

  return (
    <div className={`relative ${urgent ? 'bg-amber-50 border-amber-200' : 'bg-brand-50 border-brand-200'} border-b px-4 py-2`}>
      <div className="flex items-center justify-center gap-3 text-sm">
        <Sparkles className={urgent ? 'h-4 w-4 text-amber-600' : 'h-4 w-4 text-brand-600'} />
        <span className={urgent ? 'text-amber-900' : 'text-brand-900'}>
          {daysLeft > 0
            ? <>Você está no <strong>trial gratuito</strong> · {daysLeft} {daysLeft === 1 ? 'dia restante' : 'dias restantes'}</>
            : <>Seu trial expira <strong>hoje</strong></>}
          <Link to="/billing" className="ml-2 underline font-medium hover:no-underline">
            Ativar agora →
          </Link>
        </span>
        {!urgent && (
          <button onClick={() => setDismissed(true)} className="absolute right-3 opacity-50 hover:opacity-100">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
